import { useQuery } from "@tanstack/react-query";
import { getCountries, type Country } from "../api/publicApi";
import { normalizeNationality, NATIONALITY_ALIAS_TO_ISO } from "../utils/nationalityAliases";

/**
 * Country/nationality master list (ISO 3166-1 alpha-2 + display name + flag).
 * Backed by the public `/country` endpoint which is shared with the mobile app.
 * Cached for 24h — the list almost never changes.
 */
export const useNationalities = () =>
  useQuery<Country[], Error>({
    queryKey: ["nationalities"],
    queryFn: () => getCountries(),
    staleTime: 1000 * 60 * 60 * 24,
    gcTime: 1000 * 60 * 60 * 24,
  });

// The names an OCR reads off a Spanish ID ("MARRUECOS", "ALEMANIA") are the CLDR Spanish region names, which the
// browser already knows for every country; the curated alias table only covers the common ones.
let spanishNames: Intl.DisplayNames | null | undefined;
const spanishName = (code: string): string | undefined => {
  if (spanishNames === undefined) {
    try {
      spanishNames = new Intl.DisplayNames(['es'], { type: 'region' });
    } catch {
      spanishNames = null;
    }
  }
  try {
    return spanishNames?.of(code.toUpperCase()) ?? undefined;
  } catch {
    return undefined;
  }
};

/**
 * Resolve a stored nationality value into a `Country` from the canonical
 * `/country` list. Returns undefined when there's no match — callers fall
 * back to displaying the raw string.
 *
 * Matches, in order: ISO alpha-2 code, canonical English name, legacy flag
 * URL, and finally an alias table (Spanish names, English/Spanish demonyms,
 * ISO alpha-3) so the many ways OCR records a nationality — "REPUBLICA
 * DOMINICANA", "Dominican Republic", "DO", "CANADIAN", "MEX" — all collapse to
 * one country. Returns undefined when nothing matches; callers fall back to the
 * raw string.
 */
export const resolveNationality = (
  raw: string | null | undefined,
  countries: Country[] | undefined,
): Country | undefined => {
  if (!raw || !countries?.length) return undefined;
  const trimmed = raw.trim();
  if (!trimmed) return undefined;

  const byCode = (code: string) =>
    countries.find((c) => c.country_code.toUpperCase() === code.toUpperCase());

  // 1. Direct ISO alpha-2 match.
  if (trimmed.length === 2) {
    const m = byCode(trimmed);
    if (m) return m;
  }

  // 2. Case-insensitive canonical (English) country name.
  const lower = trimmed.toLowerCase();
  const byName = countries.find((c) => c.country.toLowerCase() === lower);
  if (byName) return byName;

  // 3. Legacy: some old records stored the flag URL itself
  //    (e.g. https://.../flags/4x3/do.svg). Match the exact URL first, then
  //    fall back to extracting the 2-letter code from the file name.
  if (trimmed.startsWith("http")) {
    const byFlag = countries.find((c) => c.country_flag === trimmed);
    if (byFlag) return byFlag;
    const match = trimmed.match(/\/([a-z]{2})\.svg$/i);
    if (match) {
      const m = byCode(match[1]);
      if (m) return m;
    }
  }

  // 4. Alias table: Spanish names, demonyms (EN/ES), ISO alpha-3.
  const aliasIso = NATIONALITY_ALIAS_TO_ISO[normalizeNationality(trimmed)];
  if (aliasIso) {
    const m = byCode(aliasIso);
    if (m) return m;
  }

  // 5. The Spanish name of any country in the list (what a Spanish ID prints).
  const normalized = normalizeNationality(trimmed);
  const bySpanishName = countries.find((c) => {
    const es = spanishName(c.country_code);
    return es !== undefined && normalizeNationality(es) === normalized;
  });
  if (bySpanishName) return bySpanishName;

  return undefined;
};

/**
 * Resolve any stored nationality to its ISO alpha-2 code, or undefined when it
 * can't be resolved. Use at write time so new records are stored uniformly.
 */
export const toNationalityIsoCode = (
  raw: string | null | undefined,
  countries: Country[] | undefined,
): string | undefined => resolveNationality(raw, countries)?.country_code;

/**
 * Display label for a stored nationality value. Returns the matched country
 * name when it resolves, otherwise the raw value (legacy tolerant display).
 */
export const formatNationality = (
  raw: string | null | undefined,
  countries: Country[] | undefined,
): string => {
  if (!raw) return "—";
  const m = resolveNationality(raw, countries);
  if (m) return m.country;
  // Legacy rows stored the flag URL itself: never show a URL; the file name is the ISO code.
  if (/^https?:/i.test(raw.trim())) {
    const code = raw.trim().match(/\/([a-z]{2})\.svg(?:[?#].*)?$/i)?.[1];
    return code ? code.toUpperCase() : "—";
  }
  return raw;
};
